import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, MAJANS_TENANT_ID } from '../config/auth';

// ---------------------------------------------------------------------------
// Delegated Microsoft Graph mail access for the CRM correspondence summary.
// THE BROWSER reads the signed-in user's OWN mailbox (Mail.Read) — no
// application-level mail permission exists anywhere in this app, and adding one
// would widen app-magic too (shared app registration). Retrieval is scoped by
// CORRESPONDENT (the customer's CRM contact addresses) and a time window, never
// by content search.
//
// Ported from app-magic/app/src/services/graphMail.ts. Mail.Read is already
// admin-consented tenant-wide on the shared app reg (1b0c305e-…), so the silent
// path should always win — but the InteractionRequiredAuthError → popup fallback
// stays, in case that grant is ever narrowed.
//
// Deliberately not built on config/auth.ts#acquireApiToken(): that helper
// hardcodes the app's own API scope and falls back to a full-page REDIRECT on any
// failure, which would throw away the user's unsaved CRM state. Graph is a second
// resource, so it needs its own token with its own scopes, and only
// InteractionRequiredAuthError should trigger interaction.
// ---------------------------------------------------------------------------

const GRAPH_MAIL_SCOPES = ['Mail.Read'];
const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

/** How far back we look for customer correspondence. */
export const CORRESPONDENCE_LOOKBACK_MONTHS = 6;

/** Client-side per-body cap; the API enforces the same server-side. */
const MAX_BODY_CHARS = 2000;
const MAX_MESSAGES = 100;

export interface Correspondent {
  address: string;
  /** Display name off the header — the only name source that needs no extra Graph scope. */
  name: string | null;
}

export interface ContactEmailMessage {
  subject: string;
  from: string;
  /** Sender's display name, for 065 suggested contacts. */
  fromName: string | null;
  receivedDateTime: string;
  body: string;
  /** Stable per thread — the API groups on it so one thread = one activity. */
  conversationId: string | null;
  /** 065: everyone on the message, so a person we do not hold as a contact is still visible. */
  recipients: Correspondent[];
}

export interface CustomerCorrespondence {
  messages: ContactEmailMessage[];
  windowFrom: string;
  windowTo: string;
}

async function getGraphToken(): Promise<string> {
  // Same Majans-tenant account-pick order as acquireApiToken — a user with
  // several Microsoft accounts cached must not summarise the wrong mailbox.
  const all = msalInstance.getAllAccounts();
  const account =
    msalInstance.getActiveAccount()
    ?? all.find((a) => a.tenantId === MAJANS_TENANT_ID)
    ?? all[0];
  if (!account) {
    throw new Error('No signed-in account — sign in to read your mailbox.');
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: GRAPH_MAIL_SCOPES, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const result = await msalInstance.acquireTokenPopup({ scopes: GRAPH_MAIL_SCOPES, account });
      return result.accessToken;
    }
    throw err;
  }
}

interface GraphMessage {
  subject?: string | null;
  from?: { emailAddress?: { address?: string | null; name?: string | null } | null } | null;
  receivedDateTime?: string | null;
  uniqueBody?: { content?: string | null } | null;
  conversationId?: string | null;
  toRecipients?: GraphRecipient[] | null;
  ccRecipients?: GraphRecipient[] | null;
}

interface GraphRecipient {
  emailAddress?: { address?: string | null; name?: string | null } | null;
}

const correspondentsOf = (rs: GraphRecipient[] | null | undefined): Correspondent[] =>
  (rs ?? [])
    .map(r => ({
      address: (r.emailAddress?.address ?? '').trim().toLowerCase(),
      name: (r.emailAddress?.name ?? '').trim() || null,
    }))
    .filter(r => r.address.length > 0);

async function searchMessages(token: string, kql: string): Promise<Response> {
  const params = new URLSearchParams({
    $search: `"${kql}"`,
    $top: String(MAX_MESSAGES),
    $select: 'subject,from,receivedDateTime,uniqueBody,conversationId,toRecipients,ccRecipients',
  });
  return fetch(`${GRAPH_BASE}/me/messages?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      Prefer: 'outlook.body-content-type="text"',
    },
  });
}

/**
 * Fetch the signed-in user's correspondence with a customer's contacts over the
 * lookback window, oldest first, bodies as plain text.
 *
 * - `participants:<email>` covers from/to/cc in one query — Graph doesn't
 *   support `$filter` on recipient collections. Several contacts are OR'd, since
 *   an account is rarely one conversation with one person.
 * - **`received>=` is in the query, not applied afterwards.** `$top` caps at 100
 *   by Graph's own relevance ranking, so filtering the date client-side meant the
 *   cap bit across the WHOLE mailbox first and in-window mail from a heavily
 *   emailed contact could be silently dropped (found by Matthew in app-magic,
 *   2026-07-29). The client-side date filter below is kept as belt-and-braces.
 * - `uniqueBody` (+ the text-content Prefer header) returns only each message's
 *   NEW content, so long quoted chains don't explode the payload.
 *
 * Addresses are validated (not escaped) before reaching this KQL string — see
 * isSafeEmailAddress in api/src/utils/crmCorrespondence.ts, mirrored by
 * isSafeContactEmail below.
 */
export async function fetchCustomerCorrespondence(
  emails: string[],
): Promise<CustomerCorrespondence> {
  const addresses = emails.map((e) => (e ?? '').trim()).filter(isSafeContactEmail);
  if (addresses.length === 0) throw new Error('No contact email address to search.');

  const token = await getGraphToken();

  const windowTo = new Date();
  const windowFrom = new Date(windowTo);
  windowFrom.setMonth(windowFrom.getMonth() - CORRESPONDENCE_LOOKBACK_MONTHS);
  const receivedAfter = windowFrom.toISOString().slice(0, 10);

  /*
   * CONTACTS ONLY in the query. 065 added `participants:<bare domain>` to widen the search for
   * suggested contacts; Graph answers that with a **500** ("Unrecognized filter of type
   * Microsoft.Exchange.Data.OrFilter"), not a 400 — verified directly against the live mailbox
   * on 2026-09-17 — so the whole correspondence summary died. My first fix added a fallback
   * ladder that only stepped down on 400, which is why Matthew still saw the error.
   *
   * The widening is gone rather than repaired, because it was never needed: the messages this
   * query already returns carry to/cc, and anyone at the customer's domain who is on a thread
   * with a known contact is in that list. So suggested contacts are found by FILTERING what we
   * already have (server-side, domain-gated) instead of asking Graph a question it rejects.
   * That also makes the candidates better — people actually in the conversation, not everyone
   * who ever emailed the domain.
   */
  const participants = addresses.map((a) => `participants:${a}`).join(' OR ');
  const scoped = addresses.length > 1 ? `(${participants})` : participants;

  // Date bound server-side where possible: $top caps at 100 by Graph's own relevance ranking,
  // so filtering the window client-side lets the cap bite across the whole mailbox first
  // (found by Matthew in app-magic, 2026-07-29). 400 = Graph disliked the shape, so the
  // undated form is worth a try; anything else is about the caller or the service and
  // retrying just burns their quota.
  let res = await searchMessages(token, `${scoped} AND received>=${receivedAfter}`);
  let lastDetail = '';
  if (!res.ok) {
    lastDetail = await res.text().catch(() => '');
    if (res.status === 400) res = await searchMessages(token, scoped);
  }
  if (!res.ok) lastDetail = await res.text().catch(() => lastDetail);
  if (!res || !res.ok) {
    throw new Error(`Could not read your mailbox (Graph ${res.status}). ${lastDetail.slice(0, 200)}`);
  }
  const data = (await res.json()) as { value?: GraphMessage[] };

  const fromIso = windowFrom.toISOString();
  const messages: ContactEmailMessage[] = (data.value ?? [])
    .filter((m): m is GraphMessage & { receivedDateTime: string } =>
      typeof m.receivedDateTime === 'string' && m.receivedDateTime >= fromIso,
    )
    .map((m) => ({
      subject: m.subject ?? '(no subject)',
      from: (m.from?.emailAddress?.address ?? 'unknown').trim().toLowerCase(),
      fromName: (m.from?.emailAddress?.name ?? '').trim() || null,
      receivedDateTime: m.receivedDateTime,
      body: (m.uniqueBody?.content ?? '').trim().slice(0, MAX_BODY_CHARS),
      conversationId: m.conversationId ?? null,
      recipients: [...correspondentsOf(m.toRecipients), ...correspondentsOf(m.ccRecipients)],
    }))
    .filter((m) => m.body.length > 0)
    .sort((a, b) => a.receivedDateTime.localeCompare(b.receivedDateTime));

  return { messages, windowFrom: fromIso, windowTo: windowTo.toISOString() };
}

/* ── Calendar (migration 056) ───────────────────────────────────────────────
 *
 * Matthew asked for Recent Activity to pick up meetings as well as email.
 *
 * ⚠ Unlike Mail.Read, `Calendars.Read` is NOT on the shared app registration and
 * is NOT admin-consented (verified 2026-08-04). Until someone grants it, this
 * returns null and the run proceeds on email alone.
 *
 * Deliberately SILENT-ONLY — no acquireTokenPopup fallback. Two reasons: an
 * unexpected consent popup mid-page is hostile, and in a tenant where user
 * consent is disabled the popup would fail anyway. Calendar is an enhancement,
 * so it must never be able to break the correspondence summary.
 */

const GRAPH_CALENDAR_SCOPES = ['Calendars.Read'];
const EVENTS_PER_PAGE = 150;
/* Paging bound. calendarView is filtered CLIENT-side (Graph can't filter on an
 * attendee address), so a single capped page would spend its whole budget on the
 * rest of your diary and silently drop this customer's meetings — the exact
 * failure Matthew found in the mail path on 2026-07-29. Follow @odata.nextLink
 * instead, with a hard stop so a heavy calendar can't hang the page. */
const MAX_EVENT_PAGES = 8;

export interface CalendarEvent {
  iCalUId: string;
  subject: string;
  start: string;
  end: string | null;
  location: string | null;
  isOnlineMeeting: boolean;
  organizer: string | null;
  attendees: string[];
  body: string;
}

interface GraphEvent {
  iCalUId?: string | null;
  subject?: string | null;
  start?: { dateTime?: string | null } | null;
  end?: { dateTime?: string | null } | null;
  location?: { displayName?: string | null } | null;
  isOnlineMeeting?: boolean | null;
  isCancelled?: boolean | null;
  organizer?: { emailAddress?: { address?: string | null } | null } | null;
  attendees?: Array<{ emailAddress?: { address?: string | null } | null }> | null;
  bodyPreview?: string | null;
}

/**
 * The signed-in user's meetings over the window, filtered to those involving at
 * least one of the given contacts.
 *
 * `/me/calendarView` expands recurring series into occurrences, which is what we
 * want — three of a fortnightly catch-up are three things that happened. The
 * server keys on iCalUId, so occurrences of one series collapse to one activity
 * rather than spamming the feed.
 *
 * Returns null when the calendar can't be read (no consent) so the caller can
 * carry on with email and say so, rather than failing the whole run.
 */
export async function fetchCustomerCalendar(
  emails: string[],
  monthsBack = CORRESPONDENCE_LOOKBACK_MONTHS,
): Promise<{ events: CalendarEvent[]; truncated: boolean } | null> {
  const wanted = new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean));
  if (wanted.size === 0) return null;

  const all = msalInstance.getAllAccounts();
  const account =
    msalInstance.getActiveAccount()
    ?? all.find((a) => a.tenantId === MAJANS_TENANT_ID)
    ?? all[0];
  if (!account) return null;

  let token: string;
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: GRAPH_CALENDAR_SCOPES, account });
    token = result.accessToken;
  } catch {
    return null; // not consented — email-only run
  }

  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - monthsBack);
  const params = new URLSearchParams({
    startDateTime: start.toISOString(),
    endDateTime: end.toISOString(),
    $top: String(EVENTS_PER_PAGE),
    $orderby: 'start/dateTime desc',
    $select: 'iCalUId,subject,start,end,location,isOnlineMeeting,isCancelled,organizer,attendees,bodyPreview',
  });

  const raw: GraphEvent[] = [];
  let url: string | null = `${GRAPH_BASE}/me/calendarView?${params.toString()}`;
  let truncated = false;
  for (let page = 0; url; page++) {
    if (page >= MAX_EVENT_PAGES) { truncated = true; break; }
    let res: Response;
    try {
      res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', Prefer: 'outlook.timezone="Australia/Brisbane"' },
      });
    } catch {
      return page === 0 ? null : { events: toCalendarEvents(raw, wanted), truncated: true };
    }
    if (!res.ok) return page === 0 ? null : { events: toCalendarEvents(raw, wanted), truncated: true };
    const data = (await res.json()) as { value?: GraphEvent[]; '@odata.nextLink'?: string };
    raw.push(...(data.value ?? []));
    url = data['@odata.nextLink'] ?? null;
  }
  return { events: toCalendarEvents(raw, wanted), truncated };
}

/** Keep only meetings that actually involve this customer — the rest of the
 *  diary never leaves the browser. */
function toCalendarEvents(source: GraphEvent[], wanted: Set<string>): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const e of source) {
    if (!e?.iCalUId || e.isCancelled || !e.start?.dateTime) continue;
    const organizer = e.organizer?.emailAddress?.address?.toLowerCase() ?? null;
    const attendees = (e.attendees ?? [])
      .map((a) => a?.emailAddress?.address?.toLowerCase())
      .filter((a): a is string => !!a);
    // Only meetings that actually involve this customer leave the browser — the
    // rest of the diary is none of the API's business.
    if (![...attendees, organizer ?? ''].some((a) => wanted.has(a))) continue;
    events.push({
      iCalUId: e.iCalUId,
      subject: e.subject ?? '(no subject)',
      start: e.start.dateTime,
      end: e.end?.dateTime ?? null,
      location: e.location?.displayName || null,
      isOnlineMeeting: !!e.isOnlineMeeting,
      organizer,
      attendees,
      body: (e.bodyPreview ?? '').trim().slice(0, MAX_BODY_CHARS),
    });
  }
  return events;
}

/*
 * Strict address shape, kept in lockstep with the server's isSafeEmailAddress.
 * These values are interpolated into the KQL `$search` string above, so a value
 * containing a quote, paren or space could break the query or change its meaning.
 * Reject rather than escape.
 */
const SAFE_EMAIL = /^[^\s"'()<>@,;:\\]+@[^\s"'()<>@,;:\\]+\.[^\s"'()<>@,;:\\]+$/;

export function isSafeContactEmail(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 320 && SAFE_EMAIL.test(value.trim());
}

/*
 * Domain gating (065). Two rules, and both matter:
 *  - the shape must be safe to interpolate into KQL, same reject-don't-escape rule as above;
 *  - a PUBLIC mailbox domain is never a "customer domain". Treating gmail.com as Woolworths'
 *    domain would pull in every personal address the user has ever emailed. Our own domain is
 *    excluded for the same reason in reverse — colleagues are not customer contacts.
 */
const SAFE_DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const PUBLIC_MAILBOX_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'live.com.au',
  'msn.com', 'yahoo.com', 'yahoo.com.au', 'ymail.com', 'icloud.com', 'me.com', 'mac.com',
  'bigpond.com', 'bigpond.com.au', 'bigpond.net.au', 'optusnet.com.au', 'iinet.net.au',
  'tpg.com.au', 'aol.com', 'proton.me', 'protonmail.com', 'gmx.com', 'mail.com',
  'majans.com',
]);

/** Accepts a bare domain, a full URL or an email address, and returns the bare domain. */
export function normaliseDomain(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')   // scheme
    .replace(/^.*@/, '')            // an email address
    .replace(/^www\./, '')
    .replace(/[/?#].*$/, '')        // path, query, fragment
    .replace(/:\d+$/, '');         // port
}

export function isCustomerDomain(value: unknown): value is string {
  const d = normaliseDomain(value);
  return d.length > 0 && d.length <= 253 && SAFE_DOMAIN.test(d) && !PUBLIC_MAILBOX_DOMAINS.has(d);
}
